import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { BoxProps } from '@mui/material';
import { usePositionService } from 'domain/shared/employee';
import { useTechnologyService } from 'domain/shared/career';

interface EmployeePositionWithSpecializationProps extends BoxProps {
  positionId?: string;
  specializationInfo?: {
    technologyId?: string;
    competencyLevel?: string;
  };
}

const EmployeePositionWithSpecialization: React.FC<EmployeePositionWithSpecializationProps> = ({
  positionId,
  specializationInfo,
  sx,
  ...boxProps
}) => {
  const { findPositionById } = usePositionService();
  const { findById } = useTechnologyService();
  const [position, setPosition] = useState<Nullable<string>>(null);
  const [technology, setTechnology] = useState<Nullable<string>>(null);

  useEffect(() => {
    (async () => {
      if (positionId) {
        const positionResult = await findPositionById(positionId);
        setPosition(positionResult.name);
      }

      if (specializationInfo?.technologyId) {
        const technologyResult = await findById(specializationInfo.technologyId);
        setTechnology(technologyResult.name);
      } else {
        setTechnology(null);
      }
    })();
  }, [positionId, specializationInfo?.technologyId, findPositionById, findById]);

  if (!position && !technology) {
    return null;
  }

  return (
    <Box
      {...boxProps}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          '& > span': { fontSize: '12px', lineHeight: '12px' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {position && (
        <Typography component="span" variant="body2">
          {position}
        </Typography>
      )}
      {technology && (
        <Typography component="span" variant="body2">
          {technology}
        </Typography>
      )}

      {specializationInfo?.competencyLevel && (
        <Typography component="span" variant="body2">
          {` (${specializationInfo.competencyLevel})`}
        </Typography>
      )}
    </Box>
  );
};

export default EmployeePositionWithSpecialization;
